import { Box, Stack, Typography } from '@mui/material'
import { QuestionMarkCircledIcon } from '@radix-ui/react-icons'
import { useMemo } from 'react'

import { PLGHandler } from './shadCN/PLGHandler'
import { CustomTooltip } from './ui/tooltip'

import { AnnotationInfoIcon } from '@/assets/icons/annotation-info.icon'
import { CheckboxUI } from '@/components/CheckboxUI'
import { FEEDBACK_PLANS } from '@/constants/plans/variables-plans'
import { withPermissionCheck, type WithPermissionProps } from '@/hoc/WithPermissionCheck'
import { FeedbackTypesCard } from '@/models/card-activity/card-activity.model'
import { BarChartIcon, ClipboardIcon } from '@/views/NewBuilder/components/IconsBuilder'

const FEEDBACK_OPTIONS_CARD: {
  title: (showProficiencyLevel?: boolean) => string
  description: string
  icon: JSX.Element
  value: FeedbackTypesCard
  permissions: string
  tooltip: string
}[] = [
  {
    title: () => 'Proficiency Level',
    description: 'AI estimates student proficiency based on the selected standard (ACTFL, WIDA, or custom).',
    icon: <BarChartIcon color="#fff" height={11} width={11} />,
    value: FeedbackTypesCard.PROFICIENCY_LEVEL,
    permissions: [], //FEEDBACK_PLANS.FEEDBACK_GRADING_STANDARDS,
    tooltip:
      'AI evaluates student responses to estimate proficiency based on the selected standard (ACTFL, WIDA, or custom).',
  },
  {
    title: showProficiencyLevel => (!showProficiencyLevel ? 'Proficiency Level' : 'WIDA'),
    description: 'AI estimates student proficiency based on WIDA standards',
    icon: <BarChartIcon color="#fff" height={11} width={11} />,
    value: FeedbackTypesCard.WIDA,
    permissions: [], //FEEDBACK_PLANS.FEEDBACK_GRADING_STANDARDS,
    tooltip:
      'AI evaluates student responses to estimate proficiency based on WIDA standards, helping track language growth over time.',
  },
  {
    title: () => 'Grammar Insights',
    description: 'AI detects and corrects grammar errors',
    icon: <AnnotationInfoIcon color="#fff" height={11} width={11} />,
    value: FeedbackTypesCard.GRAMMAR_INSIGHTS,
    permissions: [], //FEEDBACK_PLANS.FEEDBACK_GRAMMAR_INSIGHTS,
    tooltip: 'AI detects grammar mistakes and suggests corrections.',
  },
  {
    title: () => 'Suggested Response',
    description: 'AI provides an ideal answer',
    icon: <ClipboardIcon color="#fff" height={11} width={11} />,
    value: FeedbackTypesCard.SUGGESTED_RESPONSE,
    permissions: [], //FEEDBACK_PLANS.FEEDBACK_SUGGESTED_RESPONSE,
    tooltip: 'AI provides an ideal answer based on the prompt.',
  },
]

export const getFeedbackOptionsCard = (lang: string) => {
  return FEEDBACK_OPTIONS_CARD.map(option => {
    const isWida = option.value === FeedbackTypesCard.WIDA
    const isActfl = option.value === FeedbackTypesCard.ACTFL

    if (isWida) {
      return null
    }

    if (isActfl) {
      return null
    }

    return option
  }).filter(option => option !== null)
}

const checkFeedbackIsChecked = (feedbacks: FeedbackTypesCard[], feedback: FeedbackTypesCard) => {
  return feedbacks.includes(feedback)
}

const handleFeedbackCheck = (feedbacks: FeedbackTypesCard[], feedback: FeedbackTypesCard) => {
  let newFeedbacks = [...feedbacks]

  const hasACTFL = feedbacks.includes(FeedbackTypesCard.ACTFL)
  const hasWIDA = feedbacks.includes(FeedbackTypesCard.WIDA)

  if (hasACTFL && feedback === FeedbackTypesCard.WIDA) {
    newFeedbacks = feedbacks.filter(f => f !== FeedbackTypesCard.ACTFL)
  }

  if (hasWIDA && feedback === FeedbackTypesCard.ACTFL) {
    newFeedbacks = feedbacks.filter(f => f !== FeedbackTypesCard.WIDA)
  }

  if (checkFeedbackIsChecked(feedbacks, feedback)) {
    return newFeedbacks.filter(f => f !== feedback)
  } else {
    return [...newFeedbacks, feedback]
  }
}

export function FeedbackCheckboxes({
  language,
  disabledItem,
  feedbacks,
  handleFeedbacks,
  fontSize = '12px',
  showTooltip = true,
  showIcon = true,
  showProficiencyLevel = false,
}: {
  language: string
  disabledItem?: boolean
  feedbacks: FeedbackTypesCard[]
  handleFeedbacks: (feedbacks: FeedbackTypesCard[]) => void
  fontSize?: string
  showTooltip?: boolean
  showIcon?: boolean
  showProficiencyLevel?: boolean
}) {
  return (
    <>
      {getFeedbackOptionsCard(language).map(item => (
        <FeedbackCheckboxesItem
          key={item.value}
          disabledItem={disabledItem}
          fontSize={fontSize}
          handlePointerDown={value => {
            const newFeedbacks = handleFeedbackCheck(feedbacks, value)

            handleFeedbacks(newFeedbacks)
          }}
          isChecked={checkFeedbackIsChecked(feedbacks, item.value)}
          item={item}
          requiredPermission={item.permissions}
          showIcon={showIcon}
          showProficiencyLevel={showProficiencyLevel}
          showTooltip={showTooltip}
        />
      ))}
    </>
  )
}

interface CardCheckFeedbackProps {
  item: (typeof FEEDBACK_OPTIONS_CARD)[0]
  isChecked: boolean
  handlePointerDown: (value: FeedbackTypesCard) => void
  disabledItem?: boolean
  showTooltip?: boolean
  showIcon?: boolean
  fontSize?: string
  showProficiencyLevel?: boolean
}

export function FeedbackCheckboxesItem({
  requiredPermission,
  ...props
}: {
  requiredPermission: string | string[]
} & WithPermissionProps<CardCheckFeedbackProps>) {
  const Component = useMemo(() => withPermissionCheck(ItemFeedbackCheckboxes, requiredPermission), [requiredPermission])

  return <Component {...props} />
}

function ItemFeedbackCheckboxes({
  item,
  isChecked,
  handlePointerDown,
  showTooltip = true,
  showIcon = true,
  fontSize = '12px',
  showProficiencyLevel = false,
  ...rest
}: WithPermissionProps<CardCheckFeedbackProps>) {
  return (
    <Stack key={item.value} alignItems="center" borderRadius="16px" direction="row" gap="4px" width="100%">
      {rest.disabled ? (
        <PLGHandler
          style={{
            minHeight: '16px',
            minWidth: '16px',
          }}
          visible={rest.disabled}
          onMouseEnter={rest.handleOpenPopover}
        />
      ) : (
        <CheckboxUI
          checked={isChecked}
          disabled={rest.disabledItem ?? false}
          handlePointerDown={e => {
            e.stopPropagation()
            handlePointerDown(item.value)
          }}
          size={18}
        />
      )}
      <Stack alignItems="center" direction="row" gap="4px">
        {showIcon ? (
          <Box
            sx={{
              padding: '4px',
              borderRadius: '8px',
              border: '2px solid #F4F3FF',
              backgroundColor: '#344054',
              mr: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </Box>
        ) : null}

        <Typography
          sx={{
            color: '#344054',
            fontWeight: 'medium',
            fontSize: fontSize,
          }}
        >
          {item.title(showProficiencyLevel)}
        </Typography>
        {showTooltip ? (
          <CustomTooltip placement="top" title={item.description}>
            <QuestionMarkCircledIcon color="#98A2B3" />
          </CustomTooltip>
        ) : null}
      </Stack>
    </Stack>
  )
}
